/**
 * In-flight de-duplication for a read, generalised (TG-4,
 * m1-exposure-detail-sourcing) from its original single-purpose home —
 * `WorkingCopyPort.readConfirmedSections` — to also cover
 * `OnFilePort.readOnFile` and `RenewalDataPort.readAccount`: a journey
 * load issues at most one request per port, not one per caller that
 * happens to want the same data at mount.
 *
 * This is NOT a value cache — it never remembers a resolved read past the
 * moment it settles, and it holds nothing across a reload (module state
 * only; a fresh page load starts with an empty map). What it does do: if
 * two callers ask to read the SAME renewalId through the SAME exported
 * deduper while a read for that renewalId is already in flight, the
 * second caller is handed the SAME promise rather than causing a second
 * network round trip. That is the read-side analogue of
 * `working-copy-client.ts`'s write latch, for the same reason — this
 * feature's landing route (AC-9) and resume computation can both want
 * the same read at mount without either needing to know the other is
 * asking.
 *
 * ADR-20260826-94qx: "nothing on screen is replaced by server state"
 * after a save attempt. This module is deliberately the ONLY read-side
 * helper in this feature, and `working-copy-client.ts` (the write /
 * confirm path) has no import of it and never calls it. A save can
 * therefore never trigger, populate or invalidate anything here — the
 * guarantee holds by construction (there is no code path from a write
 * into this file), not by a runtime check this module would have to get
 * right. Because no resolved value is ever retained, there is also no
 * stale-cache risk for a caller that reads again later in the same
 * session — every read after the in-flight window closes is a genuine
 * new network call, reflecting whatever the server currently holds.
 *
 * Each exported deduper below owns its OWN in-flight map — three
 * independent de-duplication windows, one per port/operation, never
 * shared. A renewalId in flight for `readOnFileDeduped` has no bearing on
 * whether `readAccountDeduped` shares or issues a fresh request for that
 * same renewalId.
 */
import type { ReadOutcome } from "./outcome";
import type { OnFilePort, OnFileReadResult } from "./on-file-port";
import type { AccountReadResult, RenewalDataPort } from "./renewal-data-port";
import type { WorkingCopyPort, WorkingCopyReadResult } from "./working-copy-port";

/**
 * Builds one independent in-flight-de-duplicating wrapper around
 * `read(port, renewalId)`. Each call to this factory owns its own
 * `Map` — callers never share one de-duplication window across two
 * different reads built from two different factory calls.
 */
function createDedupedRead<TPort, TResult>(
  read: (port: TPort, renewalId: string) => Promise<ReadOutcome<TResult>>,
): (port: TPort, renewalId: string) => Promise<ReadOutcome<TResult>> {
  const readsInFlight = new Map<string, Promise<ReadOutcome<TResult>>>();

  return (port: TPort, renewalId: string): Promise<ReadOutcome<TResult>> => {
    const inFlight = readsInFlight.get(renewalId);
    if (inFlight !== undefined) {
      return inFlight;
    }

    const pending = read(port, renewalId).finally(() => {
      readsInFlight.delete(renewalId);
    });
    readsInFlight.set(renewalId, pending);
    return pending;
  };
}

/**
 * Reads the confirmed sections for a renewal, sharing an in-flight request
 * across concurrent callers for the same renewalId. Never caches a
 * resolved value beyond the in-flight window.
 */
export const readConfirmedSectionsDeduped: (
  port: WorkingCopyPort,
  renewalId: string,
) => Promise<ReadOutcome<WorkingCopyReadResult>> = createDedupedRead((port, renewalId) =>
  port.readConfirmedSections(renewalId),
);

/**
 * Reads a renewal's on-file sections, sharing an in-flight request across
 * concurrent callers for the same renewalId. Never caches a resolved
 * value beyond the in-flight window.
 */
export const readOnFileDeduped: (port: OnFilePort, renewalId: string) => Promise<ReadOutcome<OnFileReadResult>> =
  createDedupedRead((port, renewalId) => port.readOnFile(renewalId));

/**
 * Reads a renewal's stored account record, sharing an in-flight request
 * across concurrent callers for the same renewalId. Never caches a
 * resolved value beyond the in-flight window.
 */
export const readAccountDeduped: (
  port: RenewalDataPort,
  renewalId: string,
) => Promise<ReadOutcome<AccountReadResult>> = createDedupedRead((port, renewalId) => port.readAccount(renewalId));
